import { useState, useEffect, useRef, useCallback } from "react";
import axios from "../../../config/axios";
import useBeaconControl from "../../../hooks/useBeaconControl";

/* ============================================================
   BeaconDemo v5 — advertiser demo mode for the band+ page.

   Wake choreography (Eric's spec, 8/31):
     Demo opens ("ready"): CANCEL → 2s → WAKE_UP_SLOW every 5s,
       capped at 60s total. START or EXIT stops the loop with a
       CANCEL (EXIT's CANCEL comes from the exit sequence).
     START ("countdown"): CANCEL at press, then WAKE_UP_FAST
       every 2s through the countdown. Tail is timed so:
       last FAST → 2s → CANCEL (fires exactly as the video
       starts) → 2s → first CSV cue (which sits at +2000ms).
     Countdown screen DISSOLVES (0.9s fade) into the playing
       video.

   Video parking is ENFORCED until the show phase (player5's
   autostart kick loop can override a single pause).

   Cue engine: vodtime {cur,dur} anchors every 500ms + 150ms
   interpolating ticker; pause → CANCEL + cues gated; any seek
   (±30 or scrub commit) → CANCEL + fired-set rebuild.

   Display controls = VodGalleryDisplay clone (tap-reveal center
   cluster, scrubbable bar, VOL stack). TV = VodGalleryTV-style
   (PAUSE/EXIT stack, left/right ±30, non-scrub bar).

   Usage:
     <BeaconDemo device={device} isTv={false} onExit={...} />
   ============================================================ */

const PLAYER = "https://vod.mywaitserver.live/player5.html";
const DEMO_SRC_TV = "https://vod.mywaitserver.live/shows/demo/hea/master.m3u8"; // 1080p
const DEMO_SRC_DISPLAY = "https://vod.mywaitserver.live/shows/demo/hea720b/master.m3u8"; // 720p, 2.5 Mbps cap
const DEMO_VOL = 60; // piano parity
const CSV_URL = "/firework/hea.csv";

/* ---- Ready-screen slow-wake loop ---- */
const READY_SLOW_START_MS = 2000; // CANCEL → first SLOW gap
const READY_SLOW_INTERVAL_MS = 5000; // SLOW cadence
const READY_SLOW_MAX_MS = 60000; // loop cap — stops after 1 minute

/* ---- Countdown ----
   FAST every 2s; last FAST lands at 2s-left so the tail is
   last FAST → 2s → CANCEL (at zero, video starts) → 2s → first cue. */
const COUNTDOWN_SECONDS = 10;
const COUNTDOWN_CUES = [
  { t: 8, fx: "WAKE_UP_FAST", vib: "OFF" },
  { t: 6, fx: "WAKE_UP_FAST", vib: "OFF" },
  { t: 4, fx: "WAKE_UP_FAST", vib: "OFF" },
  { t: 2, fx: "WAKE_UP_FAST", vib: "OFF" }, // last FAST — 2s before the CANCEL
];
const COUNTDOWN_FADE_MS = 900; // dissolve into the video

const TICK_MS = 150; // local cue-runner tick between vodtime anchors
const SKIP_SEC = 30;
const SEEK_JUMP_THRESHOLD_S = 2.5; // anchor jump beyond wall-time = a seek
const GREEN = "#3ADF3C";

/* ---- CSV parsing — copied verbatim from useBeaconShow.js ---- */
function parseCsvTime(t) {
  const [m, s] = String(t || "")
    .trim()
    .split(":")
    .map(Number);
  if (!Number.isFinite(m) || !Number.isFinite(s)) return NaN;
  return (m * 60 + s) * 1000;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/);
  return lines
    .slice(1)
    .map((line) => {
      const cols = line.split(",");
      const t = cols[0]?.trim();
      const fx = cols[1]?.trim();
      const vib = cols[2]?.trim() || "OFF";
      if (!t || !fx) return null;
      const offsetMs = parseCsvTime(t);
      if (isNaN(offsetMs)) return null;
      return { offsetMs, fx, vib };
    })
    .filter(Boolean)
    .sort((a, b) => a.offsetMs - b.offsetMs);
}

/* ---- Clock formatting — copied from the VOD galleries ---- */
function fmtClock(sec) {
  sec = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s2 = sec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s2).padStart(2, "0");
  return h > 0 ? h + ":" + mm + ":" + ss : m + ":" + ss;
}

/* ---- Raw transport — same endpoints as MagicBand.jsx ---- */
const postShowCmd = (deviceId, fx, vib) =>
  axios
    .post("/esp/beacon-show", { deviceId, fx, vib }, { withCredentials: true })
    .catch((err) => console.error("[BeaconDemo] cmd failed:", fx, err));

const postCancelRaw = (deviceId) =>
  axios
    .post("/esp/cancel", { deviceId }, { withCredentials: true })
    .catch((err) => console.error("[BeaconDemo] cancel failed:", err));

const BeaconDemo = ({ device, isTv = false, onExit }) => {
  const { isController, claim } = useBeaconControl();
  const isControllerRef = useRef(isController);
  useEffect(() => {
    isControllerRef.current = isController;
  }, [isController]);

  
  // Demo tap = intent to run the bands: take control on open.
  // Uses raw claim() (not ensureControl, which yields to another holder).
  // Retries only while ids aren't ready yet; never loops on a real failure.
  // The ready-screen wake loop restarts itself when isController flips true.
  const claimAttemptedRef = useRef(false);
  useEffect(() => {
    if (isController || claimAttemptedRef.current) return;
    claim().then((r) => {
      if (r.ok) {
        claimAttemptedRef.current = true;
        console.log("[BeaconDemo] control claimed on demo open");
      } else if (r.reason === "no_beacon" || r.reason === "no_device") {
        console.log("[BeaconDemo] claim waiting for device ids:", r.reason);
      } else {
        claimAttemptedRef.current = true;
        console.warn("[BeaconDemo] control claim failed:", r.reason);
      }
    });
  }, [claim, isController]);

  const deviceId = device?.magic_beacon_device?._id ?? null;
  const deviceIdRef = useRef(deviceId);
  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  /* Gated senders — no-op unless this device holds beacon control. */
  const sendCmd = useCallback((fx, vib) => {
    const did = deviceIdRef.current;
    if (!did || !isControllerRef.current) return Promise.resolve();
    console.log(`[BeaconDemo] send fx=${fx} vib=${vib}`);
    return postShowCmd(did, fx, vib);
  }, []);
  const sendCancel = useCallback(() => {
    const did = deviceIdRef.current;
    if (!did || !isControllerRef.current) return Promise.resolve();
    console.log("[BeaconDemo] send CANCEL");
    return postCancelRaw(did);
  }, []);

  const [phase, setPhase] = useState("ready"); // "ready" | "countdown" | "show"
  const [countLeft, setCountLeft] = useState(COUNTDOWN_SECONDS);
  const [countdownFade, setCountdownFade] = useState(false); // dissolve overlay
  const [isPaused, setIsPaused] = useState(false);
  const [focusedBtn, setFocusedBtn] = useState(isTv ? "start" : null);
  const [vodTime, setVodTime] = useState({ cur: 0, dur: 0 });
  // TV bar
  const [showBar, setShowBar] = useState(false);
  // Display controls (piano VodGalleryDisplay behavior)
  const [showCtl, setShowCtl] = useState(true);
  const [vol, setVol] = useState(DEMO_VOL);
  const [dragT, setDragT] = useState(null); // while finger-scrubbing
  const [skipFlash, setSkipFlash] = useState(null); // { dir, id }

  const cuesRef = useRef([]);
  const firedRef = useRef(new Set());
  const anchorRef = useRef(null); // { cur, dur, at, advancing }
  const pausedRef = useRef(false);
  const parkedRef = useRef(false); // video paused at 0 behind the screens
  const endedRef = useRef(false);
  const phaseRef = useRef("ready");
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  const frameRef = useRef(null);
  const dragTRef = useRef(null);
  // ready-screen slow loop timers
  const readySlowStartRef = useRef(null);
  const readySlowIntervalRef = useRef(null);
  const readySlowCapRef = useRef(null);
  // countdown timers
  const countdownTimerRef = useRef(null);
  const countdownFiredRef = useRef(new Set());
  const fadeTimerRef = useRef(null);
  const tickerRef = useRef(null);
  const barTimerRef = useRef(null);
  const ctlTimerRef = useRef(null);
  const skipTimerRef = useRef(null);

  /* ---- Load the HEA CSV once ---- */
  useEffect(() => {
    fetch(CSV_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`CSV fetch failed: ${r.status}`);
        return r.text();
      })
      .then((text) => {
        cuesRef.current = parseCsv(text);
        firedRef.current = new Set();
        console.log(`[BeaconDemo] Loaded ${cuesRef.current.length} cues`);
      })
      .catch((err) => console.error("[BeaconDemo] CSV load error:", err));
  }, []);

  const sendToPlayer = useCallback((msg) => {
    try {
      frameRef.current?.contentWindow?.postMessage(msg, "*");
    } catch (e) {}
  }, []);

  /* ---- Ready-screen slow-wake loop ----
     CANCEL → 2s → SLOW every 5s, capped at 60s. Stopped (with a
     CANCEL) by START; EXIT's CANCEL comes from the exit sequence. */
  const stopReadyWake = () => {
    if (readySlowStartRef.current) {
      clearTimeout(readySlowStartRef.current);
      readySlowStartRef.current = null;
    }
    if (readySlowIntervalRef.current) {
      clearInterval(readySlowIntervalRef.current);
      readySlowIntervalRef.current = null;
    }
    if (readySlowCapRef.current) {
      clearTimeout(readySlowCapRef.current);
      readySlowCapRef.current = null;
    }
  };

  useEffect(() => {
    if (phase !== "ready") return;
    sendCancel();
    readySlowStartRef.current = setTimeout(() => {
      readySlowStartRef.current = null;
      sendCmd("WAKE_UP_SLOW", "OFF");
      readySlowIntervalRef.current = setInterval(
        () => sendCmd("WAKE_UP_SLOW", "OFF"),
        READY_SLOW_INTERVAL_MS,
      );
    }, READY_SLOW_START_MS);
    readySlowCapRef.current = setTimeout(() => {
      readySlowCapRef.current = null;
      stopReadyWake();
      console.log("[BeaconDemo] ready slow-wake loop hit 60s cap — stopped");
    }, READY_SLOW_MAX_MS);
    return () => stopReadyWake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sendCmd, sendCancel]);

  /* ---- Fired-set rebuild (skip-past pattern, no burst-firing) ---- */
  const rebuildFired = (curS) => {
    const next = new Set();
    cuesRef.current.forEach((c, i) => {
      if (c.offsetMs <= curS * 1000) next.add(i);
    });
    firedRef.current = next;
    console.log(
      `[BeaconDemo] fired set rebuilt at ${curS.toFixed(1)}s (${next.size} marked past)`,
    );
  };

  /* ---- vodtime anchors from player5 (every 500ms) ---- */
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev && ev.data;
      if (!d || !d.vodtime) return;
      const now = Date.now();
      const prev = anchorRef.current;
      const cur = d.vodtime.cur || 0;
      const dur = d.vodtime.dur || 0;
      let advancing = false;
      if (prev) {
        const delta = cur - prev.cur;
        const wallS = (now - prev.at) / 1000;
        if (delta < -0.5 || delta > wallS + SEEK_JUMP_THRESHOLD_S) {
          rebuildFired(cur); // seek detected
        }
        advancing = delta > 0.005;
      }
      anchorRef.current = { cur, dur, at: now, advancing };
      setVodTime({ cur, dur });

      // ENFORCED PARKING: before the "show" phase the video must never
      // play. player5's autostart kick loop can override a single pause,
      // so any advancing frame before showtime gets re-paused + re-parked.
      if (phaseRef.current !== "show" && advancing) {
        sendToPlayer({ vodctl: "toggle" }); // pause
        sendToPlayer({ vodctl: "seekto", t: 0 });
        if (!parkedRef.current) {
          parkedRef.current = true;
          console.log("[BeaconDemo] preload parked at 0 (enforced)");
        } else {
          console.log("[BeaconDemo] park enforcement: re-paused stray playback");
        }
      }

      // End of video → run the exit sequence once.
      if (
        phaseRef.current === "show" &&
        !endedRef.current &&
        dur > 0 &&
        cur >= dur - 1
      ) {
        endedRef.current = true;
        runExit();
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- Cue runner: 150ms ticker interpolating between anchors ---- */
  useEffect(() => {
    if (phase !== "show") return;
    tickerRef.current = setInterval(() => {
      if (pausedRef.current) return;
      const a = anchorRef.current;
      if (!a) return;
      const est = a.advancing ? a.cur + (Date.now() - a.at) / 1000 : a.cur;
      const estMs = est * 1000;
      cuesRef.current.forEach((c, i) => {
        if (!firedRef.current.has(i) && c.offsetMs <= estMs) {
          firedRef.current.add(i);
          console.log(
            `[BeaconDemo] Firing +${c.offsetMs}ms: fx=${c.fx} vib=${c.vib}`,
          );
          sendCmd(c.fx, c.vib);
        }
      });
    }, TICK_MS);
    return () => {
      if (tickerRef.current) {
        clearInterval(tickerRef.current);
        tickerRef.current = null;
      }
    };
  }, [phase, sendCmd]);

  /* ---- Countdown end: CANCEL exactly as the video starts, then
          dissolve the countdown screen into the playing show. ---- */
  const beginShowPlayback = () => {
    sendCancel(); // last FAST was 2s ago; first CSV cue lands 2s from now
    firedRef.current = new Set();
    sendToPlayer({ vodctl: "seekto", t: 0 });
    if (parkedRef.current) sendToPlayer({ vodctl: "toggle" }); // play
    sendToPlayer({ vodctl: "vol", v: DEMO_VOL });
    setVol(DEMO_VOL);
    setPhase("show");
    setFocusedBtn(isTv ? "exit" : null);
    pokeCtl(false); // display controls visible on entry, then auto-hide
    // dissolve: keep the countdown screen mounted, fading, for ~0.9s
    setCountdownFade(true);
    fadeTimerRef.current = setTimeout(() => {
      fadeTimerRef.current = null;
      setCountdownFade(false);
    }, COUNTDOWN_FADE_MS);
    console.log("[BeaconDemo] countdown done — CANCEL sent, show playback started");
  };

  useEffect(() => {
    if (phase !== "countdown") return;
    const endsAt = Date.now() + COUNTDOWN_SECONDS * 1000;
    countdownFiredRef.current = new Set();
    setCountLeft(COUNTDOWN_SECONDS);

    countdownTimerRef.current = setInterval(() => {
      const msLeft = endsAt - Date.now();
      const secsLeft = Math.max(0, Math.ceil(msLeft / 1000));
      setCountLeft(secsLeft);

      COUNTDOWN_CUES.forEach((c, i) => {
        if (!countdownFiredRef.current.has(i) && secsLeft <= c.t) {
          countdownFiredRef.current.add(i);
          if (c.cmd === "CANCEL") sendCancel();
          else sendCmd(c.fx, c.vib || "OFF");
        }
      });

      if (msLeft <= 0) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        beginShowPlayback();
      }
    }, 200);

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* ---- TV bar visibility ---- */
  const pokeBar = useCallback((paused) => {
    setShowBar(true);
    if (barTimerRef.current) clearTimeout(barTimerRef.current);
    if (!paused) {
      barTimerRef.current = setTimeout(() => setShowBar(false), 2500);
    }
  }, []);

  /* ---- Display controls visibility (piano pattern) ---- */
  const pokeCtl = useCallback((pausedNow) => {
    setShowCtl(true);
    if (ctlTimerRef.current) clearTimeout(ctlTimerRef.current);
    const paused = pausedNow !== undefined ? pausedNow : pausedRef.current;
    if (!paused) {
      ctlTimerRef.current = setTimeout(() => {
        if (dragTRef.current === null) setShowCtl(false); // never hide mid-drag
      }, 3000);
    }
  }, []);

  const flashSkip = useCallback((dir) => {
    setSkipFlash({ dir, id: Date.now() });
    if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    skipTimerRef.current = setTimeout(() => setSkipFlash(null), 800);
  }, []);

  /* ---- Controls ---- */
  const handleStart = () => {
    if (phase !== "ready") return;
    stopReadyWake();
    sendCancel(); // spec: START cancels the slow loop
    setPhase("countdown"); // countdown effect fires FAST every 2s
  };

  const togglePlay = () => {
    sendToPlayer({ vodctl: "toggle" });
    setIsPaused((p) => {
      const np = !p;
      pausedRef.current = np;
      if (np) sendCancel(); // bands dark while paused
      if (isTv) pokeBar(np);
      else pokeCtl(np);
      return np;
    });
  };

  const skip = (dir) => {
    sendCancel(); // clear the current effect; cues resume at new position
    sendToPlayer({ vodctl: "seek", delta: dir * SKIP_SEC });
    flashSkip(dir);
    if (isTv) pokeBar(pausedRef.current);
    else pokeCtl();
  };

  const changeVol = (delta) => {
    setVol((v) => {
      const nv = Math.max(0, Math.min(100, v + delta));
      sendToPlayer({ vodctl: "vol", v: nv });
      return nv;
    });
  };

  const updateDrag = (t) => {
    dragTRef.current = t;
    setDragT(t);
    if (ctlTimerRef.current) clearTimeout(ctlTimerRef.current);
    setShowCtl(true);
  };

  const commitSeek = (t) => {
    sendCancel(); // bands clear across the jump
    rebuildFired(t); // deterministic: skipped cues ignored from the new spot
    sendToPlayer({ vodctl: "seekto", t });
    dragTRef.current = t;
    setDragT(t);
    setTimeout(() => {
      dragTRef.current = null;
      setDragT(null);
    }, 1200);
    pokeCtl();
  };

  const runExit = () => {
    stopReadyWake();
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    const did = deviceIdRef.current;
    if (did && isControllerRef.current) {
      postCancelRaw(did).then(() => {
        setTimeout(() => postShowCmd(did, "SOLID_OFF", "OFF"), 1000);
      });
    }
    onExit?.();
  };

  /* ---- TV remote nav ---- */
  useEffect(() => {
    if (!isTv) return;
    const handleKeyDown = (e) => {
      const kc = e.keyCode;
      const isUp = e.key === "ArrowUp" || kc === 38;
      const isDown = e.key === "ArrowDown" || kc === 40;
      const isLeft = e.key === "ArrowLeft" || kc === 37;
      const isRight = e.key === "ArrowRight" || kc === 39;
      const isSelect =
        e.key === "Enter" || e.key === "OK" || e.key === "Select" ||
        kc === 13 || kc === 23 || kc === 66;
      const isBack = e.key === "Escape" || kc === 27 || kc === 461 || kc === 10009;

      if (phase === "ready") {
        if (isUp || isDown || isLeft || isRight) {
          e.preventDefault(); e.stopPropagation();
          setFocusedBtn((b) => (b === "start" ? "exit" : "start"));
        } else if (isSelect) {
          e.preventDefault(); e.stopPropagation();
          if (focusedBtn === "start") handleStart();
          else runExit();
        } else if (isBack) {
          e.preventDefault(); e.stopPropagation();
          runExit();
        }
        return;
      }

      if (phase === "countdown") {
        // countdown is hands-off; back bails out entirely
        if (isBack) {
          e.preventDefault(); e.stopPropagation();
          runExit();
        }
        return;
      }

      // phase === "show"
      if (isLeft || isRight) {
        e.preventDefault(); e.stopPropagation();
        skip(isRight ? 1 : -1);
      } else if (isUp || isDown) {
        e.preventDefault(); e.stopPropagation();
        setFocusedBtn((b) => (b === "exit" ? "playpause" : "exit"));
      } else if (isSelect) {
        e.preventDefault(); e.stopPropagation();
        if (focusedBtn === "playpause") togglePlay();
        else runExit();
      } else if (isBack) {
        e.preventDefault(); e.stopPropagation();
        runExit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv, phase, focusedBtn, isPaused]);

  /* ---- Unmount safety net ---- */
  useEffect(() => {
    return () => {
      stopReadyWake();
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      if (tickerRef.current) clearInterval(tickerRef.current);
      if (barTimerRef.current) clearTimeout(barTimerRef.current);
      if (ctlTimerRef.current) clearTimeout(ctlTimerRef.current);
      if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    };
  }, []);

  const focusRing = (name) =>
    isTv && focusedBtn === name
      ? {
          outline: `6px solid ${GREEN}`,
          outlineOffset: "2px",
          boxShadow: `0 0 14px rgba(58,223,60,.8)`,
        }
      : {};

  const barCur = dragT !== null ? dragT : vodTime.cur;

  /* ================= RENDER ================= */

  return (
    <div style={S.overlay}>
      {/* Player mounts immediately — muted (vol=0), parked at 0 and
          ENFORCED parked until the show phase begins. */}
      <iframe
        ref={frameRef}
        title="Beacon Demo Player"
        src={`${PLAYER}?ui=tv&vol=0&src=${encodeURIComponent(isTv ? DEMO_SRC_TV : DEMO_SRC_DISPLAY)}`}
        allow="autoplay; fullscreen"
        allowFullScreen
        style={S.frame}
      />
       
      {/* rendition tag — which R2 path this box is playing (diag; delete later) */}
      <div style={S.srcTag}>
        {((isTv ? DEMO_SRC_TV : DEMO_SRC_DISPLAY).match(/shows\/demo\/([^/]+)\//) || [])[1] || "?"}
        {isTv ? " · tv" : " · display"}
      </div>

      {phase === "ready" && (
        <div style={S.readyInner}>
          <div style={S.readyEyebrow}>MAGIC BEACON DEMO</div>
          <h1 style={S.readyTitle}>Get Ready for the Show</h1>
          <p style={S.readySub}>
            Put on your MagicBands
            <span className="bd-dots"><span>.</span><span>.</span><span>.</span></span>
          </p>
          <div style={S.readyBtns}>
            <button
              style={{ ...S.bigBtn, ...S.startBtn, ...focusRing("start") }}
              onClick={handleStart}
            >
              START THE SHOW
            </button>
            <button style={{ ...S.bigBtn, ...focusRing("exit") }} onClick={runExit}>
              EXIT
            </button>
          </div>
          <style>{`
            @keyframes bdDot { 0%,60%,100%{opacity:.2} 30%{opacity:1} }
            .bd-dots span{ animation: bdDot 1.4s infinite; }
            .bd-dots span:nth-child(2){ animation-delay:.2s; }
            .bd-dots span:nth-child(3){ animation-delay:.4s; }
          `}</style>
        </div>
      )}

      {(phase === "countdown" || countdownFade) && (
        <div
          className={countdownFade ? "bd-cd-fade" : ""}
          style={{ ...S.readyInner, zIndex: 9300 }}
        >
          <div style={S.readyEyebrow}>MAGIC BEACON DEMO</div>
          <h2 style={S.countLabel}>SHOW STARTS IN</h2>
          <div key={countLeft} className="bd-count" style={S.countNum}>
            {countLeft}
          </div>
          <style>{`
            @keyframes bdCountPop {
              0%{ transform:scale(1.6); opacity:.2; }
              25%{ transform:scale(1); opacity:1; }
              100%{ transform:scale(.94); opacity:1; }
            }
            .bd-count{ animation: bdCountPop .95s ease-out forwards; }
            @keyframes bdCdFade {
              0%{ opacity:1; }
              100%{ opacity:0; }
            }
            .bd-cd-fade{ animation: bdCdFade ${COUNTDOWN_FADE_MS}ms ease forwards;
              pointer-events:none; }
          `}</style>
        </div>
      )}

      {phase === "show" && isTv && (
        <>
          {/* ===== TV controls — VodGalleryTV-style ===== */}
          {skipFlash && (
            <div key={skipFlash.id} className="bdSkipFlash" style={S.skipFlashTV}>
              {skipFlash.dir < 0 && (
                <svg viewBox="0 0 24 24" style={S.skipSvgTV} fill="white">
                  <path d="M11 18V6l-8.5 6L11 18zm.5-6l8.5 6V6l-8.5 6z" />
                </svg>
              )}
              <span style={S.skipTextTV}>{skipFlash.dir > 0 ? "+30" : "-30"}</span>
              {skipFlash.dir > 0 && (
                <svg viewBox="0 0 24 24" style={S.skipSvgTV} fill="white">
                  <path d="M13 6v12l8.5-6L13 6zm-.5 6L4 6v12l8.5-6z" />
                </svg>
              )}
            </div>
          )}

          {showBar && vodTime.dur > 0 && (
            <div style={S.progWrapTV}>
              <span style={S.progTimeTV}>{fmtClock(vodTime.cur)}</span>
              <div style={S.progTrack}>
                <div
                  style={{
                    ...S.progFill,
                    width: `${Math.min(100, (vodTime.cur / vodTime.dur) * 100)}%`,
                  }}
                />
              </div>
              <span style={S.progTimeTV}>{fmtClock(vodTime.dur)}</span>
            </div>
          )}

          <div style={S.btnStackTV}>
            <button
              style={{ ...S.ctlBtnTV, ...focusRing("playpause") }}
              onClick={togglePlay}
            >
              {isPaused ? "PLAY" : "PAUSE"}
            </button>
            <button style={{ ...S.ctlBtnTV, ...focusRing("exit") }} onClick={runExit}>
              EXIT
            </button>
          </div>
        </>
      )}

      {phase === "show" && !isTv && (
        <>
          {/* ===== Display controls — cloned from VodGalleryDisplay ===== */}
          <div
            style={S.tapLayer}
            onClick={() => (showCtl ? setShowCtl(false) : pokeCtl())}
          />

          {skipFlash && (
            <div key={skipFlash.id} className="bdSkipFlash" style={S.skipFlashD}>
              {skipFlash.dir < 0 && (
                <svg viewBox="0 0 24 24" style={S.skipSvgD} fill="white">
                  <path d="M11 18V6l-8.5 6L11 18zm.5-6l8.5 6V6l-8.5 6z" />
                </svg>
              )}
              <span style={S.skipTextD}>{skipFlash.dir > 0 ? "+30" : "-30"}</span>
              {skipFlash.dir > 0 && (
                <svg viewBox="0 0 24 24" style={S.skipSvgD} fill="white">
                  <path d="M13 6v12l8.5-6L13 6zm-.5 6L4 6v12l8.5-6z" />
                </svg>
              )}
            </div>
          )}

          {showCtl && (
            <>
              <div style={S.centerCluster}>
                <button style={S.roundBtn} onClick={() => skip(-1)}>
                  <svg viewBox="0 0 24 24" style={{ width: 34, height: 34 }} fill="white">
                    <path d="M11 18V6l-8.5 6L11 18zm.5-6l8.5 6V6l-8.5 6z" />
                  </svg>
                  <span style={S.roundBtnLabel}>30</span>
                </button>
                <button style={S.bigPlayBtn} onClick={togglePlay}>
                  {isPaused ? (
                    <svg viewBox="0 0 24 24" style={{ width: 54, height: 54 }} fill="white">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" style={{ width: 54, height: 54 }} fill="white">
                      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                    </svg>
                  )}
                </button>
                <button style={S.roundBtn} onClick={() => skip(1)}>
                  <svg viewBox="0 0 24 24" style={{ width: 34, height: 34 }} fill="white">
                    <path d="M13 6v12l8.5-6L13 6zm-.5 6L4 6v12l8.5-6z" />
                  </svg>
                  <span style={S.roundBtnLabel}>30</span>
                </button>
              </div>

              {vodTime.dur > 0 && (
                <div style={S.progWrapD}>
                  <span style={S.progTimeD}>{fmtClock(barCur)}</span>
                  <input
                    type="range"
                    min={0}
                    max={Math.floor(vodTime.dur)}
                    value={Math.floor(barCur)}
                    onChange={(e) => updateDrag(parseInt(e.target.value, 10))}
                    onPointerUp={(e) => commitSeek(parseInt(e.target.value, 10))}
                    onTouchEnd={(e) => { if (dragT !== null) commitSeek(dragT); }}
                    className="bdScrub"
                    style={S.scrub}
                  />
                  <span style={S.progTimeD}>{fmtClock(vodTime.dur)}</span>
                </div>
              )}
            </>
          )}

          <div style={S.rightStack}>
            <div style={S.volGroup}>
              <button style={S.stackBtn} onClick={() => changeVol(10)}>VOL +</button>
              <div style={S.volLabel}>{vol}%</div>
              <button style={S.stackBtn} onClick={() => changeVol(-10)}>VOL −</button>
            </div>
            <button style={S.stackBtn} onClick={runExit}>EXIT</button>
          </div>
        </>
      )}

      {phase === "show" && (
        <style>{`
          @keyframes bdSkipFade{
            0%{ opacity:0; transform:translate(-50%,-50%) scale(.85); }
            15%{ opacity:1; transform:translate(-50%,-50%) scale(1); }
            70%{ opacity:1; }
            100%{ opacity:0; transform:translate(-50%,-50%) scale(1.05); }
          }
          .bdSkipFlash{ animation: bdSkipFade .8s ease forwards; }
          .bdScrub{ -webkit-appearance:none; appearance:none; background:transparent; }
          .bdScrub::-webkit-slider-runnable-track{
            height:14px; border-radius:8px; background:rgba(255,255,255,0.3);
          }
          .bdScrub::-webkit-slider-thumb{
            -webkit-appearance:none; appearance:none; width:38px; height:38px; border-radius:50%;
            background:#f7c04a; border:3px solid #fff; margin-top:-12px;
            box-shadow:0 2px 10px rgba(0,0,0,.6);
          }
        `}</style>
      )}
    </div>
  );
};

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "#000",
    zIndex: 9000,
    fontFamily: "Lato, Arial, sans-serif",
  },
  frame: {
    position: "absolute",
    inset: 0,
    width: "100vw",
    height: "100vh",
        border: "none",
    pointerEvents: "none",
  },
  srcTag: {
    position: "absolute",
    left: 10,
    bottom: 6,
    zIndex: 9400,
    fontSize: 13,
    fontFamily: "Lato",
    color: "rgba(255,255,255,0.45)",
    pointerEvents: "none",
  },
  /* ---------- Get Ready + Countdown screens ---------- */
  readyInner: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 40,
    background:
      "radial-gradient(60% 50% at 50% 30%, rgba(91,22,144,.45) 0%, transparent 70%), #0b0217",
  },
  readyEyebrow: {
    fontSize: 22,
    letterSpacing: 6,
    fontWeight: 900,
    color: "#c084fc",
    marginBottom: 14,
  },
  readyTitle: {
    margin: "0 0 18px",
    fontSize: 62,
    fontWeight: 900,
    color: "#fff",
    textShadow: "0 0 24px rgba(168,85,247,.6)",
  },
  readySub: { margin: "0 0 44px", fontSize: 28, color: "#e9d5ff" },
  readyBtns: { display: "flex", gap: 24, justifyContent: "center" },
  bigBtn: {
    background: "rgba(0,0,0,0.5)",
    border: "2px solid white",
    borderRadius: 12,
    color: "white",
    fontSize: 28,
    fontWeight: 700,
    fontFamily: "Lato",
    height: 76,
    minWidth: 240,
    padding: "0 34px",
    cursor: "pointer",
    outline: "none",
  },
  startBtn: {
    background: "linear-gradient(110deg, #059669 0%, #10b981 50%, #059669 100%)",
    border: "2px solid #a7f3d0",
    boxShadow: "0 2px 12px rgba(5,150,105,0.5)",
  },
  countLabel: {
    margin: "0 0 6px",
    fontSize: 40,
    fontWeight: 900,
    letterSpacing: 4,
    color: "#fff",
    textShadow: "0 0 20px rgba(168,85,247,.6)",
  },
  countNum: {
    fontSize: 200,
    lineHeight: 1,
    fontWeight: 900,
    color: "#f7c04a",
    textShadow: "0 0 30px rgba(247,192,74,.8), 0 0 70px rgba(247,192,74,.45)",
    fontVariantNumeric: "tabular-nums",
  },
  /* ---------- TV controls ---------- */
  btnStackTV: {
    position: "absolute",
    right: 40,
    bottom: 40,
    zIndex: 9100,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    alignItems: "stretch",
  },
  ctlBtnTV: {
    background: "rgba(0,0,0,0.5)",
    border: "2px solid white",
    borderRadius: 10,
    color: "white",
    fontSize: 30,
    fontWeight: 700,
    fontFamily: "Lato",
    height: 70,
    width: 220,
    textAlign: "center",
    cursor: "pointer",
    outline: "none",
  },
  progWrapTV: {
    position: "absolute",
    left: "50%",
    bottom: 44,
    transform: "translateX(-50%)",
    zIndex: 9150,
    display: "flex",
    alignItems: "center",
    gap: 22,
    background: "rgba(0,0,0,0.6)",
    borderRadius: 16,
    padding: "18px 28px",
    width: "62vw",
    pointerEvents: "none",
  },
  progTrack: {
    flex: 1,
    height: 12,
    borderRadius: 8,
    background: "rgba(255,255,255,0.25)",
    overflow: "hidden",
  },
  progFill: {
    height: "100%",
    background: "#f7c04a",
    borderRadius: 8,
    transition: "width .4s linear",
  },
  progTimeTV: {
    color: "white",
    fontFamily: "Lato",
    fontWeight: 700,
    fontSize: 28,
    fontVariantNumeric: "tabular-nums",
    minWidth: 110,
    textAlign: "center",
  },
  skipFlashTV: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%,-50%)",
    zIndex: 9200,
    display: "flex",
    alignItems: "center",
    gap: 18,
    background: "rgba(0,0,0,0.55)",
    borderRadius: 20,
    padding: "22px 38px",
    pointerEvents: "none",
  },
  skipSvgTV: { width: 64, height: 64 },
  skipTextTV: {
    color: "white",
    fontFamily: "Lato",
    fontWeight: 900,
    fontSize: 56,
    letterSpacing: 1,
    textShadow: "0 2px 12px rgba(0,0,0,.8)",
  },
  /* ---------- Display controls (VodGalleryDisplay sizes) ---------- */
  tapLayer: { position: "absolute", inset: 0, zIndex: 9050 },
  centerCluster: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%,-50%)",
    zIndex: 9100,
    display: "flex",
    alignItems: "center",
    gap: 44,
  },
  bigPlayBtn: {
    width: 110,
    height: 110,
    borderRadius: "50%",
    cursor: "pointer",
    background: "rgba(0,0,0,0.55)",
    border: "3px solid white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  roundBtn: {
    width: 84,
    height: 84,
    borderRadius: "50%",
    cursor: "pointer",
    background: "rgba(0,0,0,0.55)",
    border: "2px solid white",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  },
  roundBtnLabel: { color: "white", fontFamily: "Lato", fontWeight: 900, fontSize: 16 },
  progWrapD: {
    position: "absolute",
    left: "50%",
    bottom: 96,
    transform: "translateX(-50%)",
    zIndex: 9100,
    display: "flex",
    alignItems: "center",
    gap: 16,
    background: "rgba(0,0,0,0.6)",
    borderRadius: 14,
    padding: "12px 20px",
    width: "78vw",
  },
  scrub: { flex: 1, height: 38, cursor: "pointer" },
  progTimeD: {
    color: "white",
    fontFamily: "Lato",
    fontWeight: 700,
    fontSize: 20,
    fontVariantNumeric: "tabular-nums",
    minWidth: 78,
    textAlign: "center",
  },
  rightStack: {
    position: "absolute",
    right: 22,
    bottom: 18,
    zIndex: 9100,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "stretch",
    width: 130,
  },
  volGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "stretch",
  },
  volLabel: {
    color: "white",
    fontFamily: "Lato",
    fontWeight: 700,
    fontSize: 18,
    textAlign: "center",
    background: "rgba(0,0,0,0.45)",
    borderRadius: 8,
    padding: "4px 0",
  },
  stackBtn: {
    background: "rgba(0,0,0,0.55)",
    border: "2px solid white",
    borderRadius: 10,
    color: "white",
    fontSize: 20,
    fontWeight: 700,
    fontFamily: "Lato",
    height: 52,
    width: "100%",
    textAlign: "center",
    cursor: "pointer",
    outline: "none",
  },
  skipFlashD: {
    position: "absolute",
    left: "50%",
    top: "28%",
    transform: "translate(-50%,-50%)",
    zIndex: 9200,
    display: "flex",
    alignItems: "center",
    gap: 14,
    background: "rgba(0,0,0,0.55)",
    borderRadius: 16,
    padding: "14px 26px",
    pointerEvents: "none",
  },
  skipSvgD: { width: 44, height: 44 },
  skipTextD: {
    color: "white",
    fontFamily: "Lato",
    fontWeight: 900,
    fontSize: 38,
    letterSpacing: 1,
    textShadow: "0 2px 12px rgba(0,0,0,.8)",
  },
};

export default BeaconDemo;
